import {useEffect, useRef, useCallback} from "react";
import Typed from "typed.js";

/**
 * Hook for typed.js on <input>, modes:
 *  - mode: 'placeholder' — type in placeholder
 *  - mode: 'value' — type in value
 */
export function useTyped(strings = [], typedOptions = {}, uiOptions = {}) {
	const {
		mode = "placeholder",
		staticPlaceholderOnFocus = "",
		resumeOnBlur = true,
		disabled = false,
		clearOnFocus = false,
	} = uiOptions;

	const inputRef = useRef(null);
	const typedRef = useRef(null);
	const initialPlaceholderRef = useRef(null);

	const teardownTyped = useCallback(() => {
		if (typedRef.current) {
			typedRef.current.destroy();
			typedRef.current = null;
		}
	}, []);

	const initTyped = useCallback(
	  (el) => {
		  if (!strings.length || !el || typedRef.current) return;
		  typedRef.current = new Typed(el, {
			  strings,
			  typeSpeed: 70,
			  backSpeed: 110,
			  backDelay: 800,
			  smartBackspace: true,
			  showCursor: true,
			  autoInsertCss: true,
			  bindInputFocusEvents: false,
			  ...(mode === "placeholder" ? {attr: "placeholder"} : {attr: "value"}),
			  ...typedOptions,
		  });
	  },
	  [JSON.stringify(strings), JSON.stringify(typedOptions), mode]
	);

	useEffect(() => {
		const el = inputRef.current;
		if (disabled || !el) return;

		if (initialPlaceholderRef.current === null) {
			initialPlaceholderRef.current = el.getAttribute("placeholder") || "";
		}

		initTyped(el);

		const onFocus = () => {
			// Destroy to avoid frozen partial text mid-animation
			teardownTyped();

			if (mode === "placeholder") {
				const ph =
				  staticPlaceholderOnFocus !== ""
					? staticPlaceholderOnFocus
					: initialPlaceholderRef.current || "";
				el.setAttribute("placeholder", ph);
			} else if (mode === "value" && clearOnFocus) {
				el.value = "";
				if (staticPlaceholderOnFocus !== "") {
					el.setAttribute("placeholder", staticPlaceholderOnFocus);
				} else if (initialPlaceholderRef.current) {
					el.setAttribute("placeholder", initialPlaceholderRef.current);
				} else {
					el.removeAttribute("placeholder");
				}
			}
		};

		const onBlur = () => {
			if (resumeOnBlur) initTyped(el);
		};

		el.addEventListener("focus", onFocus);
		el.addEventListener("blur", onBlur);

		return () => {
			el.removeEventListener("focus", onFocus);
			el.removeEventListener("blur", onBlur);
			teardownTyped();
		};
	}, [
		disabled,
		initTyped,
		teardownTyped,
		mode,
		staticPlaceholderOnFocus,
		resumeOnBlur,
		clearOnFocus,
	]);

	const start = useCallback(() => typedRef.current?.start(), []);
	const stop = useCallback(() => typedRef.current?.stop(), []);
	const reset = useCallback((clear) => typedRef.current?.reset(clear), []);
	const isActive = useCallback(() => Boolean(typedRef.current), []);

	return {inputRef, start, stop, reset, destroy: teardownTyped, isActive};
}